import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AnimatoForm from "@/components/AnimatoForm";
import RiepilogoIscrizione from "@/components/RiepilogoIscrizione";
import { AppLogo } from "@/components/Shared";
import { toast } from "sonner";

export default function IscrizionePubblica() {
  const [stato, setStato] = useState(null);
  const [inviata, setInviata] = useState(false);
  const [messaggio, setMessaggio] = useState("");
  // Il riepilogo esiste SOLO in questa schermata, in memoria: non viene
  // salvato, non viene inviato, e ricaricando la pagina sparisce. È l'unica
  // occasione in cui l'interessato può conservarne copia — l'app non manda
  // email — quindi la schermata lo dice a chiare lettere.
  const [riepilogo, setRiepilogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [attesa, setAttesa] = useState(0); // secondi residui dopo un 429

  useEffect(() => {
    api.get("/public/iscrizioni/stato").then((r) => setStato(r.data)).catch(() => setStato({ aperte: false, motivo: "Servizio non disponibile" }));
  }, []);

  // Blocco temporaneo: pulsante disabilitato fino a scadenza, nessun retry automatico
  useEffect(() => {
    if (attesa <= 0) return;
    const t = setInterval(() => setAttesa((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [attesa > 0]);

  const invia = async (payload) => {
    if (attesa > 0) return;
    setSaving(true);
    try {
      const { data } = await api.post("/public/iscrizioni", payload);
      setMessaggio(data?.messaggio || "");
      setRiepilogo(data?.riepilogo || null);
      setInviata(true);
    } catch (err) {
      if (err.response?.status === 429) setAttesa(err.retryAfter || 3600);
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const attesaLeggibile = (s) => {
    const m = Math.ceil(s / 60);
    return m > 1 ? `${m} minuti` : `${s} secondi`;
  };

  return (
    <div className="min-h-dvh bg-[#FFFDF9] py-8 px-4">
      <div className="max-w-xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center mb-6">
          <AppLogo size={64} className="mb-3 drop-shadow-lg" />
          <h1 className="font-display text-2xl font-extrabold text-slate-900 text-center">SS. Medici Oratorio Estivo</h1>
          <p className="text-sm font-semibold text-slate-500">Modulo di iscrizione</p>
          <Link to="/genitore/registrazione" className="mt-3 text-xs font-bold text-emerald-600 hover:text-emerald-700" data-testid="vai-account-genitore-link">
            Crea un account Genitore per seguire le richieste
          </Link>
        </motion.div>

        {!stato ? (
          <p className="text-center text-slate-400 font-semibold">Caricamento...</p>
        ) : inviata ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="card-app p-10 text-center" data-testid="iscrizione-inviata">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
            <h2 className="font-display text-xl font-extrabold text-slate-900 mb-2">Richiesta inviata!</h2>
            <p className="text-sm text-slate-500 font-semibold">{messaggio || "Grazie per aver inviato la richiesta! Ti aspettiamo in Parrocchia per confermare l'iscrizione. A presto!"}</p>
            {riepilogo && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-2xl p-3 font-semibold mt-5 text-left" data-testid="avviso-riepilogo">
                Qui sotto trovi il riepilogo di quanto hai dichiarato. <strong>Conservalo ora</strong>:
                stampalo o salvalo in PDF: chiudendo questa pagina non sarà più recuperabile,
                perché non viene inviato per email né conservato altrove.
              </p>
            )}
          </motion.div>
        ) : null}

        {inviata && riepilogo && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-app mt-4 print:mt-0 print:shadow-none">
            <RiepilogoIscrizione dati={riepilogo} />
          </motion.div>
        )}

        {!stato ? null : inviata ? null : !stato.aperte ? (
          <div className="card-app p-10 text-center" data-testid="iscrizioni-chiuse">
            <XCircle className="h-14 w-14 text-red-400 mx-auto mb-4" />
            <h2 className="font-display text-xl font-extrabold text-slate-900 mb-2">Iscrizioni chiuse</h2>
            <p className="text-sm text-slate-500 font-semibold">{stato.motivo}</p>
          </div>
        ) : (
          <div className="card-app p-6" data-testid="iscrizione-pubblica-form">
            {attesa > 0 && (
              <p className="rounded-2xl bg-red-50 border border-red-200 p-3 text-xs font-bold text-red-700 mb-4" data-testid="iscrizione-attesa">
                Troppi invii da questa connessione. Riprova tra {attesaLeggibile(attesa)}.
              </p>
            )}
            <AnimatoForm isPublic onSubmit={invia} saving={saving || attesa > 0}
              submitLabel={attesa > 0 ? `Attendi ${attesaLeggibile(attesa)}` : "Invia richiesta di iscrizione"} />
          </div>
        )}
      </div>
    </div>
  );
}
